import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import {
  type useUserRelationshipTypes_query$data,
  type useUserRelationshipTypes_query$key
} from '@/__generated__/useUserRelationshipTypes_query.graphql'
import { type useUserRelationshipTypesMutation } from '@/__generated__/useUserRelationshipTypesMutation.graphql'

type CommitAsyncFn = (relationshipTypeIds: string[]) => Promise<useUserRelationshipTypes_query$data>

export const useUserRelationshipTypes = (
  queryRef?: useUserRelationshipTypes_query$key | null | undefined
): [string[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useUserRelationshipTypes_query on Query {
        currentUser {
          userRelationshipTypes(orderBy: [RELATIONSHIP_TYPE_BY_RELATIONSHIP_TYPE_ID__SEQUENCE_ASC]) {
            nodes {
              relationshipTypeId
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () => map(queryData?.currentUser?.userRelationshipTypes.nodes, 'relationshipTypeId'),
    [queryData?.currentUser?.userRelationshipTypes.nodes]
  )
  const [commit, isPending] = useMutation<useUserRelationshipTypesMutation>(graphql`
    mutation useUserRelationshipTypesMutation($input: UpdateUserRelationshipTypesInput!) {
      updateUserRelationshipTypes(input: $input) {
        query {
          ...useUserRelationshipTypes_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (relationshipTypeIds) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              relationshipTypeIds
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending]
}
